import { Component, Input, ViewChild, OnInit, Output, EventEmitter } from '@angular/core';
import { takeUntil } from 'rxjs';
import { SelectControl } from '@shared/ui/controls/select-control.model';
import { ComboBoxModule, MultiSelectComponent, MultiSelectModule } from "@progress/kendo-angular-dropdowns";
import { SelectLookupService } from '../../services/select-lookup.service';
import { BaseControl } from '@shared/ui/controls/base-control.model';
import { UiColumn } from '@shared/ui/ui-column';
import { BaseComponent } from '@core/models/base-component.model';
import { ToolbarItem } from '../../interfaces/tool-bar-item.interface';
import { EntityService } from '@shared/services/entity.service';
import { WindowService, WindowSettings } from '@progress/kendo-angular-dialog';
import { EntityComponent } from '../../literals/entity-components.literal';
import { FormGroup, FormsModule } from '@angular/forms';
import { DetailService } from '@core/services/detail.service';
import { SplitButtonModule } from '@progress/kendo-angular-buttons';

export interface OnChangeArgs {
  value: any;
  previousValue: any;
  select: SelectControl;
}

@Component({
    selector: 'app-select-field',
    imports: [MultiSelectModule, ComboBoxModule, SplitButtonModule, FormsModule],
    providers: [SelectLookupService, EntityService],
    templateUrl: './select-field.component.html',
    styleUrls: ['./select-field.component.css']
})
export class SelectFieldComponent extends BaseComponent implements OnInit {

  @Input() control: BaseControl;
  @ViewChild("multiselect", { static: true }) public multiselect: MultiSelectComponent;

  // Will emit an object with previous value and new selected value // { value: value, previousValue: selectedValue }
  @Output() onChange: EventEmitter<any> = new EventEmitter();

  select: SelectControl;
  autoClose: boolean = false;
  totalWidth: number = 500;
  columns: UiColumn[] = [];

  actionLinks: ToolbarItem[];

  constructor(private service: SelectLookupService, private entityService: EntityService<any>, private windowService: WindowService, private detailService: DetailService<any>) { super(); }

  ngOnInit(): void {
    this.select = this.control as SelectControl;
    this.autoClose = !this.select.allowMultiple;

    if (this.select.allowMultiple) {
      if (this.select.value) {
        var values = this.select.value;
        this.select.value = [];
        for (var i = 0; i < values.length; i++) {
          var val = values[i];
          this.select.value.push(val);
        }
      }
    }

    if (this.select.singleColumnMember !== undefined && this.select.singleColumnMember.length > 0) {
      this.select.columns = this.select.columns.filter(s => s.field == this.select.singleColumnMember);
    }

    this.totalWidth = this.sum(this.select.columns, "size");
    if (this.totalWidth > 750) {
      this.totalWidth = 750;
    }

    this.actionLinks = [
      {
        text: "Add New",
        hasMultiple: true,
        action: () => { this.addNew() },
      },
      {
        text: "Edit",
        hasMultiple: false,
        action: () => { this.edit() }
      }
    ];
  }

  addNew() {
    var e = window.event;
    e?.preventDefault();

    var apiController = this.select.addEditLink;
    if (apiController === "") {
      return;
    }

    var entity = this.entityService.get(this.entityService.getKey(apiController, "0")); //this.entityService.getEntity();

    type e = { [key: string]: any };
    var newDataItem: e = {};

    for (var key in entity) {
      newDataItem[key] = null;
    }

    var title = apiController.replace(/([A-Z])/g, ' $1');

    var options: WindowSettings = {
      title: 'Add ' + title,
      width: 800,
      height: 500,
      content: EntityComponent[apiController],
      draggable: true,
      resizable: true,
    };

    const win = this.windowService.open(options);
    win.content.instance.entity = newDataItem;

    win.content.instance.submitForm.subscribe((formData: FormGroup) => {

      var newEntity = formData.value;
      this.detailService.save(newEntity, apiController, null, false).subscribe((resp) => {
        if (resp.wasSuccessful) {
          this.entityService.set(this.entityService.getKey(apiController, resp.entity['id']), resp.entity);
          this.entityService.trigger();
        } else {

        }
      });

    });
  }

  edit() {


    this.entityService.trigger();
  }

  addEditSubItemSelected(item: ToolbarItem) {
    if (item && item.action) {
      item.action();
    }
  }

  trackByColumn(index: number, column: UiColumn): string {
    return column.field;
  }

  sum(arr: any[], prop: string): number {
    var total = 0;

    for (var i = 0; i < arr.length; i++) {
      total += arr[i][prop];
    }

    return total;
  }

  filter(value: string) {

    var localSearch = (this.select.originalData != undefined && this.select.originalData.length > 0) && !this.select.performSearch;
    if (localSearch) {
      var filteredResults = this.select.originalData.filter(d => d[this.select.displayMember].toString().startsWith(value) || d[this.select.displayMember].toString().includes(value));
      this.select.data = filteredResults;
    } else {
      if (this.select.minimumCharacterLength != null && value.length >= this.select.minimumCharacterLength) {
        this.select.filters.filter = value;
        this.service.lookup(this.select).pipe(takeUntil(this.subscription)).subscribe(((jsonData: any) => {
          this.select.data = jsonData;
        }));
      } else if (this.select.minimumCharacterLength != null) {
        var charDiff = this.select.minimumCharacterLength - value.length;
        this.select.noResultMessage = "Enter " + charDiff + " more characters...";
      } else {
        //this.multiselect.no
        this.multiselect.toggle(false);
      }
    }

  }

  open(): void {
    if (this.select.forceDataLoadOnOpen) {
      if (this.select.data != null && this.select.data.length > 0 && !this.select.clearDataOnOpen) {
        return;
      }
      this.select.noResultMessage = "Loading Data...";
      this.service.lookup(this.select).pipe(takeUntil(this.subscription)).subscribe(((jsonData: any) => {
        this.select.data = jsonData;
      }));
    } else {
      this.select.data = [];
      if (this.select.minimumCharacterLength != null && this.select.minimumCharacterLength > 0) {
        this.select.noResultMessage = "Enter " + this.select.minimumCharacterLength + " or more characters...";
      } else {
        this.select.noResultMessage = "Enter 1 or more characters..."
      }
      //this.multiselect.noDataTemplate
    }
  }

  change(value: any): void {
    if (typeof value === 'object') {
      var selectedValue = value;
      var element = document.getElementById(this.select.id);
      if (element) {
        var children = Array.from(element.children);
        var searchBar = children.find(c => c.nodeName == "KENDO-SEARCHBAR");
        if (this.select.allowMultiple) {
          var chds = Array.from(children[0].children);
          searchBar = chds.find(c => c.nodeName == "KENDO-SEARCHBAR");
        }

        if (searchBar) {
          var input = Array.from(searchBar.children).find(s => s.nodeName == "INPUT") as HTMLInputElement;
          if (input) {
            var textValue = input.value;
            selectedValue = this.select.data.find(d => d[this.select.displayMember] == textValue);
            if (selectedValue == undefined) {
              selectedValue = this.select.previousValue;
            }
            // Clear filtered text after selection
            if (this.select.allowMultiple) {
              input.value = "";
            }
          }
        }
      }

      var args: OnChangeArgs = {
        value: value,
        previousValue: selectedValue,
        select: this.select
      }

      this.onChange.emit(args); //{ value: value, previousValue: selectedValue, select: this.select }
    }
  }

  close(): void {
    var newValue = this.select.allowMultiple ? this.select.value : this.select.singleValue;

    if (!this.select.allowMultiple && typeof newValue !== 'object') {
      setTimeout(() => {
        this.select.singleValue = this.select.originalValue;
      }, 100);
    }
  }

}
